import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useUI } from "@/context/UIContext";
import { usePermissions } from "@/lib/permissions";
import { vehicleService, driverService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SkeletonTable } from "@/components/common/Skeletons";
import { NoResults } from "@/components/common/EmptyState";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Upload, Search, AlertTriangle, ChevronRight } from "lucide-react";
import { AddVehicleDialog } from "@/components/dialogs/AddVehicleDialog";
import { BatteryStatus } from "@/components/common/BatteryStatus";
import { toast } from "sonner";

const STATUSES = ["all", "moving", "parked", "idle", "offline", "alert"];

// Minimal CSV parser for the Import button — good enough for a flat,
// unquoted export of make,model,year,vin,license_plate. No third-party
// dependency for something this small.
function parseVehicleCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",").map((c) => c.trim());
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] ?? ""; });
    return {
      make: row.make, model: row.model, year: row.year, vin: row.vin,
      licensePlate: row.license_plate || row.licenseplate || row.plate,
    };
  });
}

export default function Vehicles() {
  const navigate = useNavigate();
  const { isManager } = usePermissions();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const { facilityId } = useUI();
  const { data, loading, refetch } = useFetch(() => vehicleService.list({ status, q, facilityId }), [status, q, facilityId]);
  const vehicles = data || [];

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    const rows = parseVehicleCsv(await file.text());
    if (rows.length === 0) {
      toast.error("No rows found — expected a header row plus make,model,year,vin,license_plate columns");
      return;
    }

    setImporting(true);
    let ok = 0, failed = 0;
    for (const row of rows) {
      if (!row.make || !row.model || !row.vin) { failed++; continue; }
      try {
        await vehicleService.create(row);
        ok++;
      } catch {
        failed++;
      }
    }
    setImporting(false);
    toast[failed && !ok ? "error" : "success"](
      `Imported ${ok} vehicle${ok === 1 ? "" : "s"}${failed ? `, ${failed} failed` : ""}`
    );
    if (ok) refetch();
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Vehicles"
        subtitle="Manage and monitor every connected vehicle in your fleet."
        actions={isManager && (
          <>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportFile} />
            <Button variant="secondary" disabled={importing} onClick={() => fileInputRef.current?.click()} data-testid="import-vehicles">
              <Upload className="mr-1.5 h-4 w-4" /> {importing ? "Importing…" : "Import"}
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="add-vehicle"><Plus className="mr-1.5 h-4 w-4" /> Add Vehicle</Button>
          </>
        )}
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, VIN, plate, device…" className="h-10 pl-9" data-testid="vehicles-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-full sm:w-44" data-testid="vehicles-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={7} />
      ) : vehicles.length === 0 ? (
        <NoResults title="No vehicles found" description="Try adjusting your search or filters." />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/70 bg-card/40 lg:block">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border/60">
                  <TableHead>Vehicle</TableHead>
                  <TableHead>VIN</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Speed</TableHead>
                  <TableHead>Battery</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Alerts</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((v) => {
                  const driver = v.driverId ? driverService.byId(v.driverId) : null;
                  return (
                    <TableRow
                      key={v.id}
                      onClick={() => navigate(`/vehicles/${v.id}`)}
                      className="cursor-pointer border-border/50"
                      data-testid={`vehicle-row-${v.id}`}
                    >
                      <TableCell>
                        <div className="font-medium">{v.name}</div>
                        <div className="text-xs text-muted-foreground">{v.fleetTag} · {v.year}</div>
                      </TableCell>
                      <TableCell><span className="font-telemetry text-xs text-muted-foreground">{v.vin.slice(0, 11)}…</span></TableCell>
                      <TableCell className="text-sm">{driver ? driver.name : <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                      <TableCell><StatusBadge status={v.status} size="sm" /></TableCell>
                      <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">{v.location.address}</TableCell>
                      <TableCell className="font-telemetry text-sm">{v.status === "moving" ? `${v.speed}` : "—"}</TableCell>
                      <TableCell><BatteryStatus status={v.batteryStatus} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{relativeTime(v.lastSeen)}</TableCell>
                      <TableCell className="text-right">
                        {v.alerts > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
                            <AlertTriangle className="h-3 w-3" /> {v.alerts}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 lg:hidden">
            {vehicles.map((v) => {
              const driver = v.driverId ? driverService.byId(v.driverId) : null;
              return (
                <button key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} className="block w-full rounded-2xl border border-border/70 bg-card/50 p-4 text-left">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">{v.name}</div>
                      <div className="text-xs text-muted-foreground">{v.fleetTag} · {driver?.name || "Unassigned"}</div>
                    </div>
                    <StatusBadge status={v.status} size="sm" />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="truncate text-muted-foreground">{v.location.address}</span>
                    <BatteryStatus status={v.batteryStatus} compact />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      <AddVehicleDialog open={addOpen} onOpenChange={setAddOpen} onSaved={refetch} />
    </div>
  );
}
